/**
 *  -----------------------------------------------------------------------------
 *  Generic factory utility that creates a type‑safe React Context + Provider pair
 *  for controlling a Latitude `<Drawer />`. It drastically reduces boilerplate
 *  in feature‑level hooks such as `usePersonRolesDrawer`, `useQuorumMembersDrawer`,
 *  etc. by abstracting the repetitive state & context wiring.
 *  ----------------------------------------------------------------------------- *
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import type { DrawerProps } from '../..';
import { Drawer } from '../..';

type DrawerProvider<E = undefined> = E extends undefined
  ? {
      context?: undefined;
      flush?: () => void;
      children: React.ReactNode;
    }
  : {
      context: E;
      flush?: () => void;
      children: React.ReactNode;
    };

/**
 * Runtime context shared between provider & consumers.
 * `P` is the payload type (domain‑specific data passed when opening the drawer).
 */
type DrawerCtx<P, E> = {
  context: E;
  /** Whether the drawer is currently visible */
  isOpen: boolean;
  /** Payload currently stored in the provider (undefined when closed) */
  payload: P | undefined;
  /** Shows the drawer and stores the given payload */
  openDrawer: (data: P) => void;
  /** Hides the drawer and clears the payload */
  closeDrawer: () => void;
};

export type CreateDrawerProviderParams<P, E = undefined> = Partial<
  Omit<DrawerProps, 'title' | 'description' | 'extraHeaderContent' | 'closeOnX'>
> & {
  //* payload can be undefined when the drawer is closed
  title: (
    payload: P | undefined,
    context: E,
  ) => Pick<DrawerProps, 'title'> | string;
  description: (
    payload: P | undefined,
    context: E,
  ) => Pick<DrawerProps, 'description'> | string;
  extraHeaderContent?: (payload: P | undefined, context: E) => React.ReactNode;
  closeOnX?: (payload: P | undefined, context: E) => DrawerProps['closeOnX'];

  //* when renderContent is called, payload is guaranteed to be defined
  renderContent: (payload: P, context: E) => React.ReactNode;
};

/**
 * Factory that returns a Provider component + corresponding hook for a drawer.
 *
 * @typeParam P  – Shape of the payload that the drawer receives.
 * @param params.title         Function that derives the drawer title from payload.
 * @param params.description   Function that derives the drawer subtitle.
 * @param params.renderContent Function that renders drawer body given the payload.
 *
 * @returns `[Provider, useDrawer]` – a tuple where
 *          **Provider** wraps the subtree that can open the drawer and
 *          **useDrawer** is a hook exposing `openDrawer` / `closeDrawer` / `isOpen`.
 */
export const createDrawerProvider = <P, E = undefined>(
  {
    title,
    description,
    renderContent,
    extraHeaderContent,
    closeOnX,
    ...drawerProps
  }: CreateDrawerProviderParams<P, E>,
  contextName?: string,
) => {
  // Internal React Context carrying drawer state.
  const Ctx = createContext<DrawerCtx<P, E> | undefined>(undefined);

  /** Provider component that owns the drawer state & renders the <Drawer/>. */
  const Provider = ({ context, flush, children }: DrawerProvider<E>) => {
    // Payload doubles as the "isOpen" boolean.
    const [payload, setPayload] = useState<P | undefined>(undefined);
    const isOpen = Boolean(payload);

    // Methods ----------------------------------------------------------------
    const openDrawer = useCallback((data: P) => setPayload(data), []);
    const closeDrawer = useCallback(() => {
      setPayload(undefined);
      flush?.();
    }, [flush]);

    // Memoized context value to avoid unnecessary re‑renders.
    const ctxValue: DrawerCtx<P, E> = useMemo(() => {
      return {
        isOpen: isOpen,
        context: context as E,
        payload,
        openDrawer,
        closeDrawer,
      };
      // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, context]);

    // Memoized Drawer props value to avoid unnecessary re‑renders.
    const drawerPropsMemoized: Omit<DrawerProps, 'children'> = useMemo(
      () => {
        const headerProps: DrawerProps['header'] = drawerProps.header || {};

        const drwrProps = {
          ...drawerProps,
          header: headerProps,
        };

        if (closeOnX) {
          const hasCloseOnX = closeOnX(payload, context as E);
          if (hasCloseOnX) {
            drwrProps.header.closeOnX = hasCloseOnX;
          } else {
            drwrProps.header = {};
            drwrProps.bluredOverlay = true;
          }
        }

        return {
          ...drwrProps,
          onOpenChange: closeDrawer,
          title: <>{title(payload, context as E)}</>,
          description: <>{description(payload, context as E)}</>,
          ...(extraHeaderContent
            ? {
                extraHeaderContent: (
                  <>{extraHeaderContent(payload, context as E)}</>
                ),
              }
            : {}),
        };
      },
      // oxlint-disable-next-line react-hooks/exhaustive-deps
      [isOpen, context],
    );

    // Render provider & the Drawer portal.
    return (
      <Ctx.Provider value={ctxValue}>
        {children}
        <Drawer open={isOpen} {...drawerPropsMemoized}>
          <div>{payload && renderContent(payload, context as E)}</div>
        </Drawer>
      </Ctx.Provider>
    );
  };

  /** Hook that gives access to drawer controls inside the provider tree. */
  const useDrawer = () => {
    const str = contextName || '';

    const ctx = useContext(Ctx);
    if (!ctx) {
      throw new Error(
        `use${str}Drawer must be used within its ${str}DrawerProvider`,
      );
    }
    return ctx;
  };

  // Return typed tuple – TS infers names automatically.
  return [Provider, useDrawer] as const;
};
